import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import Loading from '@/components/shared/Loading';
import type { OutlookCalendar } from '@/hooks/useOutlookCalendars';
import type { Task } from '@/components/ui/columns-tasks';

const outlookColorMap: Record<string, { bg: string; border: string; text: string }> = {
  auto: { bg: '#4A5568', border: '#2D3748', text: '#FFFFFF' },
  lightBlue: { bg: '#63B3ED', border: '#3182CE', text: '#1A365D' },
  lightGreen: { bg: '#68D391', border: '#38A169', text: '#22543D' },
  lightOrange: { bg: '#F6AD55', border: '#DD6B20', text: '#7C2D12' },
  lightGray: { bg: '#A0AEC0', border: '#718096', text: '#1A202C' },
  lightYellow: { bg: '#F6E05E', border: '#D69E2E', text: '#744210' },
  lightTeal: { bg: '#4FD1C5', border: '#319795', text: '#234E52' },
  lightPink: { bg: '#F687B3', border: '#D53F8C', text: '#702459' },
  lightBrown: { bg: '#B7791F', border: '#975A16', text: '#FFFAF0' },
  lightRed: { bg: '#FC8181', border: '#E53E3E', text: '#742A2A' },
  maxColor: { bg: '#4A5568', border: '#2D3748', text: '#FFFFFF' },
};

function getCalendarColors(color?: string) {
  return outlookColorMap[color || 'auto'] || outlookColorMap.auto;
}

interface OutlookCalendarsSectionProps {
  task: Task;
  availableCalendars: OutlookCalendar[];
  onSyncToCalendar: (taskGuid: string, calendarId: string) => Promise<boolean>;
  onUnsyncFromCalendar: (taskGuid: string, calendarId: string, myCalendarId?: string) => Promise<boolean>;
  disabled?: boolean;
}

export function OutlookCalendarsSection({
  task,
  availableCalendars,
  onSyncToCalendar,
  onUnsyncFromCalendar,
  disabled = false,
}: OutlookCalendarsSectionProps) {
  const [syncingCalendarId, setSyncingCalendarId] = useState<string | null>(null);
  const isBusy = disabled || syncingCalendarId !== null;

  const syncedCalendars = task.synced_calendars || [];
  const syncedCalendarIds = new Set(syncedCalendars.map((sc) => sc.calendar_id));
  const unsyncedCalendars = availableCalendars.filter(
    (cal) => cal.canEdit && !syncedCalendarIds.has(cal.id),
  );

  const handleSync = async (calendarId: string) => {
    if (isBusy) return;
    setSyncingCalendarId(calendarId);
    try {
      await onSyncToCalendar(task.id, calendarId);
    } finally {
      setSyncingCalendarId(null);
    }
  };

  const handleUnsync = async (calendarId: string, myCalendarId?: string) => {
    if (isBusy) return;
    setSyncingCalendarId(calendarId);
    try {
      await onUnsyncFromCalendar(task.id, calendarId, myCalendarId);
    } finally {
      setSyncingCalendarId(null);
    }
  };

  if (syncedCalendars.length === 0 && unsyncedCalendars.length === 0) {
    return <p className="text-xs text-gray-400 italic">No calendars available</p>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {syncedCalendars.map((sync) => {
        const cal = availableCalendars.find((c) => c.id === sync.calendar_id);
        const colors = getCalendarColors(cal?.color);
        const isProcessing = syncingCalendarId === sync.calendar_id;
        return (
          <div
            key={sync.calendar_id}
            className="inline-flex items-center gap-1.5 rounded-full border bg-white py-0.5 pr-1 pl-2 text-xs"
            style={{ borderColor: colors.border }}
          >
            {sync.external_event_id ? (
              <a
                href={`https://outlook.office.com/calendar/item/${encodeURIComponent(sync.external_event_id)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5"
              >
                <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: colors.bg }} />
                <span className="max-w-30 truncate text-gray-700">{cal?.name || 'Calendar'}</span>
              </a>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: colors.bg }} />
                <span className="max-w-30 truncate text-gray-700">{cal?.name || 'Calendar'}</span>
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                const dbCalId = sync.original_calendar_id || sync.calendar_id;
                const myCalId = sync.original_calendar_id ? sync.calendar_id : undefined;
                handleUnsync(dbCalId, myCalId);
              }}
              disabled={isBusy}
              className="ml-0.5 rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-500 disabled:opacity-50"
            >
              {isProcessing ? <Loading size="sm" /> : <X className="h-3 w-3" />}
            </button>
          </div>
        );
      })}
      {unsyncedCalendars.map((cal) => {
        const colors = getCalendarColors(cal.color);
        const isProcessing = syncingCalendarId === cal.id;
        return (
          <button
            key={cal.id}
            type="button"
            onClick={() => handleSync(cal.id)}
            disabled={isBusy}
            className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-xs text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50"
          >
            {isProcessing ? <Loading size="sm" /> : <Plus className="h-2.5 w-2.5" />}
            <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: colors.bg }} />
            <span className="max-w-30 truncate">{cal.name}</span>
          </button>
        );
      })}
    </div>
  );
}
